import { useState } from 'react';

import { Card, CardContent } from '@/components/ui/card';
import { ExperimentEditorDialog } from '@/features/im-channel-proactivity-experiments/ExperimentEditorDialog';
import { ExperimentNotices } from '@/features/im-channel-proactivity-experiments/ExperimentNotices';
import { ExperimentsHeader } from '@/features/im-channel-proactivity-experiments/ExperimentsHeader';
import { ExperimentsTable } from '@/features/im-channel-proactivity-experiments/ExperimentsTable';
import type {
  ImChannelProactivityExperiment,
  ProactivenessCohortChannelFilter,
  ProactivenessSection,
  ProactivenessFilter,
} from '@/features/im-channel-proactivity-experiments/im-channel-proactivity-experiments-types';
import { RolloutEditorDialog } from '@/features/im-channel-proactivity-experiments/RolloutEditorDialog';
import { SendExperimentDialog } from '@/features/im-channel-proactivity-experiments/SendExperimentDialog';
import {
  type CohortChannelFilters,
  type CohortFlagFilters,
  type CohortPages,
  useImChannelProactivityExperimentCohort,
  useImChannelProactivityExperiments,
  useProactivenessRollouts,
  useSendImChannelProactivityExperimentNow,
  useSetImChannelProactivityExperimentPaused,
  useSyncImChannelProactivityExperimentCohort,
} from '@/features/im-channel-proactivity-experiments/useImChannelProactivityExperiments';

type DialogState = {
  mode: 'create' | 'duplicate' | 'edit';
  experiment?: ImChannelProactivityExperiment;
};

export const ImChannelProactivityExperimentsPage = ({
  section,
  onSectionChange,
}: {
  section: ProactivenessFilter;
  onSectionChange: (section: ProactivenessFilter) => void;
}) => {
  const [cohortPages, setCohortPages] = useState<CohortPages>({
    experimental: 1,
    production: 1,
  });
  const [cohortChannelFilters, setCohortChannelFilters] =
    useState<CohortChannelFilters>({
      experimental: 'all',
      production: 'all',
    });
  const [cohortFlagFilters, setCohortFlagFilters] = useState<CohortFlagFilters>(
    {
      experimental: '',
      production: '',
    }
  );
  const experimentsQuery = useImChannelProactivityExperiments(section);
  const cohortQuery = useImChannelProactivityExperimentCohort(
    section,
    cohortPages,
    cohortChannelFilters,
    cohortFlagFilters
  );
  const rolloutsQuery = useProactivenessRollouts();
  const syncCohort = useSyncImChannelProactivityExperimentCohort(section);
  const sendNow = useSendImChannelProactivityExperimentNow();
  const setPaused = useSetImChannelProactivityExperimentPaused();
  const experiments = experimentsQuery.data ?? [];
  const rollouts = (rolloutsQuery.data ?? []).filter(
    (rollout) => section === 'all' || rollout.section === section
  );
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [rolloutDialogOpen, setRolloutDialogOpen] = useState(false);
  const [experimentToSend, setExperimentToSend] =
    useState<ImChannelProactivityExperiment | null>(null);

  const handleCreate = () => setDialog({ mode: 'create' });
  const handleEdit = (experiment: ImChannelProactivityExperiment) =>
    setDialog({ mode: 'edit', experiment });
  const handleDuplicate = (experiment: ImChannelProactivityExperiment) =>
    setDialog({ mode: 'duplicate', experiment });
  const handleSync = () => syncCohort.mutate();
  const handlePauseChange = (
    experiment: ImChannelProactivityExperiment,
    isPaused: boolean
  ) =>
    setPaused.mutate({
      slug: experiment.slug,
      section: experiment.section,
      isPaused,
    });
  const handleSend = () => {
    if (!experimentToSend) return;
    sendNow.mutate(experimentToSend.slug);
    setExperimentToSend(null);
  };
  const handleDialogOpenChange = (open: boolean) => {
    if (!open) setDialog(null);
  };
  const handleCohortPageChange = (
    cohortSection: ProactivenessSection,
    page: number
  ) =>
    setCohortPages((currentPages) => ({
      ...currentPages,
      [cohortSection]: page,
    }));
  const handleCohortChannelFilterChange = (
    cohortSection: ProactivenessSection,
    channel: ProactivenessCohortChannelFilter
  ) => {
    setCohortChannelFilters((currentFilters) => ({
      ...currentFilters,
      [cohortSection]: channel,
    }));
    setCohortPages((currentPages) => ({
      ...currentPages,
      [cohortSection]: 1,
    }));
  };
  const handleCohortFlagFilterChange = (
    cohortSection: ProactivenessSection,
    flagKey: string
  ) => {
    setCohortFlagFilters((currentFilters) => ({
      ...currentFilters,
      [cohortSection]: flagKey,
    }));
    setCohortPages((currentPages) => ({
      ...currentPages,
      [cohortSection]: 1,
    }));
  };

  return (
    <>
      <Card>
        <ExperimentsHeader
          section={section}
          onSectionChange={onSectionChange}
          rollouts={rollouts}
          hasExperiments={experiments.length > 0}
          isSyncing={syncCohort.isPending}
          onCreate={handleCreate}
          onCreateRollout={() => setRolloutDialogOpen(true)}
          onSync={handleSync}
        />
        <CardContent>
          <ExperimentsTable
            experiments={experiments}
            section={section}
            isLoading={experimentsQuery.isLoading}
            error={experimentsQuery.error}
            sendingSlug={sendNow.isPending ? sendNow.variables : undefined}
            changingPauseSlug={
              setPaused.isPending ? setPaused.variables.slug : undefined
            }
            onEdit={handleEdit}
            onDuplicate={handleDuplicate}
            onSend={setExperimentToSend}
            onPauseChange={handlePauseChange}
          />
          <div className="mt-4">
            <ExperimentNotices
              rolloutsError={rolloutsQuery.error}
              cohorts={cohortQuery.data}
              cohortError={cohortQuery.error}
              syncError={syncCohort.error}
              sendResult={sendNow.data}
              sendError={sendNow.error}
              pauseError={setPaused.error}
              isCohortLoading={cohortQuery.isFetching}
              cohortChannelFilters={cohortChannelFilters}
              cohortFlagFilters={cohortFlagFilters}
              onCohortPageChange={handleCohortPageChange}
              onCohortChannelFilterChange={handleCohortChannelFilterChange}
              onCohortFlagFilterChange={handleCohortFlagFilterChange}
            />
          </div>
        </CardContent>
      </Card>
      {dialog && (
        <ExperimentEditorDialog
          key={dialog.mode + '-' + (dialog.experiment?.slug ?? 'new')}
          mode={dialog.mode}
          experiment={dialog.experiment}
          section={section}
          rollouts={rollouts}
          onOpenChange={handleDialogOpenChange}
        />
      )}
      {rolloutDialogOpen && (
        <RolloutEditorDialog
          section={section === 'all' ? 'production' : section}
          onOpenChange={setRolloutDialogOpen}
        />
      )}
      {experimentToSend && (
        <SendExperimentDialog
          experiment={experimentToSend}
          onConfirm={handleSend}
          onOpenChange={(open) => {
            if (!open) setExperimentToSend(null);
          }}
        />
      )}
    </>
  );
};
